// src/i18n.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

// Import JSON dictionaries (Vite supports JSON imports)
import en from "./locales/en.json";
import nl from "./locales/nl.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import ptBR from "./locales/pt-BR.json";
import hi from "./locales/hi.json";
import id from "./locales/id.json";
import ar from "./locales/ar.json";
import zhHans from "./locales/zh-Hans.json";
import zhHant from "./locales/zh-Hant.json";

export type SupportedLocale =
  | "en"
  | "nl"
  | "es"
  | "fr"
  | "de"
  | "pt-BR"
  | "hi"
  | "id"
  | "ar"
  | "zh-Hans"
  | "zh-Hant";

export type LocaleMeta = {
  code: SupportedLocale;
  name: string;       // English name
  nativeName: string; // Native label
};

const LOCALES: LocaleMeta[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "pt-BR", name: "Portuguese (Brazil)", nativeName: "Português (Brasil)" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "id", name: "Indonesian", nativeName: "Bahasa Indonesia" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "zh-Hans", name: "Chinese (Simplified)", nativeName: "简体中文" },
  { code: "zh-Hant", name: "Chinese (Traditional)", nativeName: "繁體中文" }
];

const STORAGE_KEY = "intimsync_locale";

type Dict = Record<string, string>;

const translations: Record<SupportedLocale, Dict> = {
  en: en as Dict,
  nl: nl as Dict,
  es: es as Dict,
  fr: fr as Dict,
  de: de as Dict,
  "pt-BR": ptBR as Dict,
  hi: hi as Dict,
  id: id as Dict,
  ar: ar as Dict,
  "zh-Hans": zhHans as Dict,
  "zh-Hant": zhHant as Dict
};

type I18nContextValue = {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  locales: LocaleMeta[];
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return Object.entries(vars).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`{${key}}`, "g"), String(value)),
    str
  );
}

function normalizeNavigatorLocale(nav: string): SupportedLocale {
  const s = (nav || "").trim();

  // Common cases first
  if (s.toLowerCase().startsWith("nl")) return "nl";
  if (s.toLowerCase().startsWith("es")) return "es";
  if (s.toLowerCase().startsWith("fr")) return "fr";
  if (s.toLowerCase().startsWith("de")) return "de";
  if (s.toLowerCase().startsWith("pt")) return "pt-BR";
  if (s.toLowerCase().startsWith("hi")) return "hi";
  if (s.toLowerCase().startsWith("id")) return "id";
  if (s.toLowerCase().startsWith("ar")) return "ar";

  // Chinese script detection
  const lower = s.toLowerCase();
  if (lower.startsWith("zh")) {
    // Rough mapping: Hans = simplified, Hant = traditional
    if (lower.includes("hant") || lower.includes("tw") || lower.includes("hk") || lower.includes("mo")) return "zh-Hant";
    return "zh-Hans";
  }

  return "en";
}

function detectInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return "en";

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as SupportedLocale | null;
    if (stored && translations[stored]) return stored;
  } catch {
    // ignore
  }

  return normalizeNavigatorLocale(navigator.language || "");
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectInitialLocale());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    return {
      locale,
      setLocale: (loc: SupportedLocale) => setLocaleState(loc),
      locales: LOCALES,
      t: (key, vars) => {
        const dict = translations[locale] || translations.en;
        const base = dict[key] ?? translations.en[key] ?? key;
        return interpolate(base, vars);
      }
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
